export interface CourseLesson {
  lessonNumber: string;
  title: string;
  duration: string;
  toolFeatured: string;
  summary: string;
  homeworkContent: string;
  isLocked: boolean;
  /** Tool key from pricing.ts — used to show upgrade banner for Starter users */
  requiredToolKey?: string;
  /** Google Drive file ID for embedded video playback */
  driveFileId?: string;
}

export interface CourseModule {
  moduleNumber: number;
  title: string;
  overview: string;
  learningOutcome: string;
  note?: string;
  lessons: CourseLesson[];
}

export interface CourseData {
  title: string;
  subtitle: string;
  modules: CourseModule[];
}

/** Map of tool display names to the tool keys in pricing.ts that gate them */
export const TOOL_KEY_MAP: Record<string, string> = {
  "Monetisation AI": "monetisation-ai",
  "Email List Builder": "email-list-builder",
  "Sales Page Builder": "sales-page-builder",
  "Launch Advisor": "launch-advisor",
  "Clip AI": "clipping-ai",
};

export const courseData: CourseData = {
  title: "From Audience to Income: The Creator Monetisation System",
  subtitle: "The step-by-step system for turning your content into consistent revenue using AI",
  modules: [
    {
      moduleNumber: 1,
      title: "The Foundation — Know Your Audience, Know Your Money",
      overview: "Before you create a single piece of content or build a single product, you need to know exactly what your audience wants to pay for. This module teaches you how to use data — not guesswork — to find the intersection between what you love creating and what people will actually buy.",
      learningOutcome: "By the end of this module, you will have identified your niche, validated a profitable topic, and built a keyword map that drives every content and product decision you make going forward.",
      lessons: [
        {
          lessonNumber: "1.1",
          title: "Why Most Creators Never Make Money (And How You Will)",
          duration: "4:11",
          toolFeatured: "None (mindset/foundation)",
          isLocked: false,
          driveFileId: "1jFk3X2yazmMAAX_hCCaNqLAK1Ij9lBi6",
          summary: "Learn why growing followers alone doesn't pay the bills and discover the 5-stage monetisation system that turns any size audience into a predictable income stream.",
          homeworkContent: `Write down your answers to these three questions. Be honest — there are no wrong answers.

1. What topic do you currently create content about, or what topic do you know more about than most people around you?

2. Who is the person you most want to help? Describe them as specifically as you can — their age, their situation, their biggest frustration.

3. What is the one thing they most want to achieve, and what is the one thing stopping them?

Keep these answers somewhere you can refer back to. We will use them in every module of this course.`
        },
        {
          lessonNumber: "1.2",
          title: "The Keyword Research Method: Finding What People Will Pay For",
          duration: "3:37",
          toolFeatured: "Keyword Research",
          isLocked: false,
          driveFileId: "1paawKB4Lg96e6_vqJhBiQ7dj4Ej4GRYk",
          summary: "Use the Keyword Research tool to find high-volume, high-intent search terms in your niche — the exact topics people are already searching for and willing to spend money on.",
          homeworkContent: `Open the Keyword Research tool and complete the following:

1. Search for your main niche topic and write down the top 5 keywords that meet the three criteria from this lesson (1,000+ monthly searches, problem or desire language, you can genuinely help).

2. For each keyword, write one sentence describing the product or piece of content you could create that would perfectly answer that search query.

3. Circle the one keyword that excites you the most. This is likely your first product topic.

Save your keyword list — you will need it in Module 3.`
        },
        {
          lessonNumber: "1.3",
          title: "Niche Positioning: How to Stand Out in a Crowded Market",
          duration: "3:18",
          toolFeatured: "AskFint",
          isLocked: false,
          driveFileId: "1OytvKJRKTQQ6eB2DYkEMX8fMRoxBf3AE",
          summary: "Craft a positioning statement that makes you the obvious choice for a specific audience, using AskFint to refine exactly who you help, what outcome you deliver, and what makes your approach different.",
          homeworkContent: `1. Open AskFint and use the prompt from this lesson to generate 3 positioning statement options for your content business.

2. Choose the one that feels most true and most specific.

3. Write your final positioning statement using the formula: "I help [specific person] achieve [specific outcome] without [common obstacle]."

4. Post your positioning statement in the Community → Content channel and ask for feedback from other members. You'll be surprised how much clarity you get from explaining it to someone else.`
        },
        {
          lessonNumber: "1.4",
          title: "Your Revenue Map: Planning Your First $1,000 Month",
          duration: "3:21",
          toolFeatured: "AskFint",
          isLocked: false,
          driveFileId: "16KDNemF0yeYWpInpxLAKWHmU3TfpvEnV",
          summary: "Reverse-engineer your first $1,000 month by calculating exactly how many sales you need, at what price, and where those buyers will come from — with AskFint building your personalised revenue plan.",
          homeworkContent: `1. Use AskFint to build your revenue map as described in this lesson.

2. Write down your three numbers: your target monthly revenue, your product price point, and the number of sales you need to hit your target.

3. Write down the answer to this question: "Where are the [X] people who need to see my offer going to come from?" Be specific — which platform, which content type, which strategy.

You now have the foundation of your business. In the next module, we're going to start building the content machine that brings those people to you.`
        }
      ]
    },
    {
      moduleNumber: 2,
      title: "The Script Machine — Creating Content That Converts",
      overview: "Content is the engine of your business. But not all content is equal. In this module, you'll learn how to use Nexis AI to create scripts that don't just get views — they attract the right audience and warm them up to buy from you.",
      learningOutcome: "By the end of this module, you will have created at least 5 complete, platform-optimised video scripts using Nexis AI, each one designed to attract your ideal buyer and lead them toward your product.",
      lessons: [
        {
          lessonNumber: "2.1",
          title: "The Content That Sells: Understanding Buyer-Aware Content",
          duration: "~8 minutes",
          toolFeatured: "Nexis AI",
          isLocked: false,
          driveFileId: "1cGkAj2tRZJU86cVDaLZc63xyuta7qUyT",
          summary: "Understand the difference between content that gets views and content that drives sales — and learn how to create 'buyer-aware' content that attracts people who are already thinking about the problem your product solves.",
          homeworkContent: `Look at your last 5 pieces of content — videos, posts, whatever you've created recently. For each one, ask yourself: "Would the person who consumed this be more likely to buy from me?" Rate each one from 1 (not at all) to 5 (very likely).

If most of your scores are 1 or 2, that's completely normal — and it's exactly why this module exists. Write down what you notice about the pattern. Are your topics too broad? Are they entertainment-focused rather than problem-focused? Keep these observations in mind as we build your new content strategy.`
        },
        {
          lessonNumber: "2.2",
          title: "Your First Script with Nexis AI",
          duration: "~12 minutes",
          toolFeatured: "Nexis AI",
          isLocked: false,
          driveFileId: "1Fe0b70i-XUqIr42YZLm-rYGzsCkGRst-",
          summary: "Walk through a hands-on demo of Nexis AI — from selecting your niche and platform to generating a full, personalised video script with a hook, structure, and monetisation-focused call to action.",
          homeworkContent: `1. Open Nexis AI and generate your first script. Use your niche, your primary platform, and a topic connected to one of your keywords from Lesson 1.2.

2. Personalise the script — add your own examples, your own voice, and a CTA that connects to your monetisation goal.

3. Record the video using this script. Don't wait for it to be perfect. The first one is always the hardest — and it gets significantly easier from here.`
        },
        {
          lessonNumber: "2.3",
          title: "The Hook Formula: Getting People to Stop Scrolling",
          duration: "~9 minutes",
          toolFeatured: "Nexis AI",
          isLocked: false,
          driveFileId: "1pDUel5Gd2rZmwZEmhnyM_IqlkHUGr4tB",
          summary: "Master the five proven hook formulas — Bold Claim, Question, Story, Number, and Transformation — that determine whether your video gets shown to 500 people or 500,000.",
          homeworkContent: `1. Go back to your last 5 pieces of content and rewrite the opening line of each one using one of the five hook formulas from this lesson.

2. Generate 3 new scripts in Nexis AI and compare the hooks it produces. Which formula does it use most often for your niche? Which one feels most natural for your voice?

3. For your next video, write 3 different hooks for the same topic and test which one performs best. This is how you develop your hook instinct over time.`
        },
        {
          lessonNumber: "2.4",
          title: "Building a 30-Day Content Calendar",
          duration: "~10 minutes",
          toolFeatured: "Nexis AI, AskFint",
          isLocked: false,
          driveFileId: "1vG9LnJnlvvo7UNL1U2DeJxgPz7p68MlN",
          summary: "Build a complete 30-day content calendar using four content pillars and Nexis AI, then use AskFint to review whether your plan effectively warms your audience up to buy.",
          homeworkContent: `1. Define your four content pillars. Write them down with a one-sentence description of each.

2. Use Nexis AI to generate at least 8 scripts — 2 per pillar — for your first two weeks of content.

3. Schedule these scripts into a content calendar. You can use a simple spreadsheet, Notion, or any scheduling tool you prefer. The goal is to have your next two weeks of content planned and ready to record before you finish this module.`
        }
      ]
    },
    {
      moduleNumber: 3,
      title: "The Product Engine — Building Your First Digital Product",
      overview: "In this module, you're going to build something you can sell. Using Monetisation AI, you'll go from a product idea to a fully defined, priced, and outlined digital product ready for creation.",
      learningOutcome: "By the end of this module, you will have a fully defined digital product — with a name, a description, a price point, and a complete outline — ready to be packaged and sold.",
      note: "The Monetisation AI tool is available on the Creator and Pro plans. If you're currently on the Starter plan and want to follow along with this module hands-on, you might find it worth exploring what the Creator plan unlocks — it's the tool that makes this entire module possible. That said, the concepts in this module apply regardless of which tool you use to build your product.",
      lessons: [
        {
          lessonNumber: "3.1",
          title: "What Sells: The Four Digital Product Types That Work for Creators",
          duration: "~9 minutes",
          toolFeatured: "Monetisation AI",
          isLocked: false,
          requiredToolKey: "monetisation-ai",
          driveFileId: "15EnewSXzs3hxfmjSiK9HAqDnNAtQCldo",
          summary: "Explore the four digital product types that consistently outperform everything else in the creator economy — ebooks, template packs, mini-courses, and coaching programmes — and learn which one to build first.",
          homeworkContent: `Based on what you learned in this lesson, write down your answers to these questions:

1. Which of the four product types feels most natural for your knowledge and your audience?

2. What specific problem would your product solve? Write it as a one-sentence problem statement: "My product helps [specific person] achieve [specific outcome] in [timeframe or context]."

3. What price point feels right for the value you'd be delivering? Look at what similar products in your niche sell for to calibrate your thinking.`
        },
        {
          lessonNumber: "3.2",
          title: "Using Monetisation AI to Build Your Product",
          duration: "~14 minutes",
          toolFeatured: "Monetisation AI",
          isLocked: false,
          requiredToolKey: "monetisation-ai",
          driveFileId: "1R9gXHF0gfeeAdOTBnohswb7UHnDCmIvu",
          summary: "Follow a hands-on walkthrough of Monetisation AI to generate fully fleshed-out product concepts — complete with names, descriptions, price points, and target audience profiles — based on your niche and keywords.",
          homeworkContent: `1. Open Monetisation AI and generate product ideas for your niche.

2. Choose the one concept that excites you most and write down: the product name, the one-sentence description, the price point, and the target audience.

3. Take your chosen concept into AskFint and ask: "What would make this product more compelling to [your target audience]? What objections might they have, and how should I address them in my marketing?" Write down the key insights.`
        },
        {
          lessonNumber: "3.3",
          title: "Pricing Your Product: The Psychology of Value",
          duration: "~8 minutes",
          toolFeatured: "AskFint",
          isLocked: false,
          driveFileId: "1IsDhy2bX7bjzjODi9YBcZGCBRCyV5Nc6",
          summary: "Discover why higher prices often lead to more sales, and learn the three factors — transformation value, market positioning, and audience ability to pay — that determine your ideal price point.",
          homeworkContent: `1. Research 5 comparable products in your niche. Write down their price points and what they include.

2. Use AskFint to validate your pricing as described in this lesson.

3. Set your final price point and write down your reasoning. Being able to articulate why your product is worth what you're charging will make you a more confident seller.`
        },
        {
          lessonNumber: "3.4",
          title: "Building Your Product Outline",
          duration: "~10 minutes",
          toolFeatured: "Monetisation AI, AskFint",
          isLocked: false,
          requiredToolKey: "monetisation-ai",
          driveFileId: "1KsXnxW48f0hVxCF0CHO3bFM4lak7YDeS",
          summary: "Use Monetisation AI to generate a section-by-section product outline, then refine it with AskFint to ensure it's outcome-focused, progressive, and delivers the full transformation you promised.",
          homeworkContent: `1. Use Monetisation AI to generate the full outline for your product.

2. Review it against the three qualities of a strong outline: outcome-focused, progressive, complete.

3. Use AskFint to get a critical review and make any necessary adjustments.

4. Write your final product outline in a document and save it. This is your production plan.`
        },
        {
          lessonNumber: "3.5",
          title: "From Outline to Product: Your Creation Sprint",
          duration: "~11 minutes",
          toolFeatured: "Monetisation AI, AskFint",
          isLocked: false,
          requiredToolKey: "monetisation-ai",
          summary: "Learn the 5-day Creation Sprint framework for building your entire digital product in one focused push — with a day-by-day plan and AskFint as your creative partner to get unstuck.",
          homeworkContent: `1. Block out your Creation Sprint in your calendar. Choose 3 to 5 consecutive days when you can dedicate at least 2 to 3 hours per day to product creation.

2. Before your sprint begins, prepare everything you need: your outline, your recording setup (if it's a video product), your writing environment (if it's an ebook), and your template files (if it's a template pack).

3. Set a completion deadline. Write it down. Tell someone about it. Accountability is one of the most powerful forces in getting a product finished.`
        }
      ]
    },
    {
      moduleNumber: 4,
      title: "The Sales System — Turning Viewers Into Buyers",
      overview: "Having a great product is not enough. You need a system that takes a stranger who just watched your video and walks them all the way to becoming a paying customer — automatically. This module builds that system: your email list, your sales page, and the automated sequence that sells for you.",
      learningOutcome: "By the end of this module, you will have a live lead magnet, a working email welcome sequence, and a published sales page for your digital product.",
      lessons: [
        {
          lessonNumber: "4.1",
          title: "Why Your Email List Is Your Most Valuable Asset",
          duration: "~9 minutes",
          toolFeatured: "Email List Builder",
          isLocked: false,
          requiredToolKey: "email-list-builder",
          summary: "Understand why your email list is 10–20x more effective than social media at reaching your audience, and learn how a lead magnet pre-qualifies subscribers into your warmest potential buyers.",
          homeworkContent: `1. Decide on your lead magnet. It should be: directly related to your paid product, immediately valuable (the subscriber gets a result just from the lead magnet), and quick to consume (a checklist, a template, a short guide — not a 50-page ebook).

2. Write a one-sentence description of your lead magnet: "Get my free [lead magnet name] — [one-sentence description of what it delivers]."

3. Write down the 3 most important things you want a new subscriber to know about you and your product within their first week on your list. These will become the foundation of your welcome sequence.`
        },
        {
          lessonNumber: "4.2",
          title: "Building Your Email List System with AI",
          duration: "~11 minutes",
          toolFeatured: "Email List Builder",
          isLocked: false,
          requiredToolKey: "email-list-builder",
          driveFileId: "1TGN84gkUuaV7MT10gpm7ZKHcJzcc-Ccn",
          summary: "Use the Email List Builder to generate your complete email infrastructure — lead magnet concept, opt-in page copy, and a 5-email automated welcome sequence that nurtures new subscribers and sells for you while you sleep.",
          homeworkContent: `1. Open the Email List Builder and generate your complete email list system.

2. Review the welcome sequence. Personalise each email with your own voice, your own stories, and specific references to your product.

3. Set up your welcome sequence in your email marketing platform of choice. If you don't have one yet, sign up for a free account on Beehiiv or Mailchimp — both have free tiers that are more than enough to get started.`
        },
        {
          lessonNumber: "4.3",
          title: "Writing a Sales Page That Converts",
          duration: "~12 minutes",
          toolFeatured: "Sales Page Builder",
          isLocked: false,
          requiredToolKey: "sales-page-builder",
          driveFileId: "1x94CiY_DjdJ4v-A1BeEZWAdekUTFkyeB",
          summary: "Use the Sales Page Builder to generate a complete, proven-structure sales page — headline, problem section, solution, features & benefits, social proof, guarantee, and call to action — ready to personalise and publish.",
          homeworkContent: `1. Open the Sales Page Builder and generate your sales page.

2. Read through the entire page as if you were your ideal buyer. At every point, ask: "Does this speak to me? Does this address my concerns? Does this make me want to buy?" Make notes on anything that feels off.

3. Personalise the copy — replace any generic language with your own voice, add your own story where relevant, and insert any specific results or testimonials you have.

4. Publish your sales page to a URL you can share. You'll need this in Module 5 for your launch.`
        },
        {
          lessonNumber: "4.4",
          title: "Setting Up Your Automated Sales Funnel",
          duration: "~10 minutes",
          toolFeatured: "Email List Builder, Sales Page Builder",
          isLocked: false,
          requiredToolKey: "email-list-builder",
          driveFileId: "1Wl-dp8EDMzZ699L6UCyDNJOgBT-KYMMs",
          summary: "Connect your lead magnet, welcome email sequence, and sales page into a single automated funnel — a system that runs 24/7 converting strangers into buyers without your involvement.",
          homeworkContent: `1. Connect your opt-in page to your email marketing platform and confirm that new subscribers are automatically added to your welcome sequence.

2. Test the entire funnel from start to finish using a test email address. Document any issues and fix them.

3. Write down your funnel URL — the link to your opt-in page. This is the link you will promote in all your content going forward.`
        },
        {
          lessonNumber: "4.5",
          title: "The Follow-Up System: Turning Warm Leads Into Buyers",
          duration: "~9 minutes",
          toolFeatured: "Email List Builder, AskFint",
          isLocked: false,
          requiredToolKey: "email-list-builder",
          driveFileId: "1oAl3fnARobTstdb1auptol0e8ItH1FRl",
          summary: "Build a post-welcome nurture sequence that continues delivering value to subscribers who haven't bought yet — providing the 5–12 touchpoints most buyers need before making a purchase decision.",
          homeworkContent: `1. Use the Email List Builder to generate a 4-email nurture sequence for subscribers who haven't purchased after 7 days.

2. Personalise the sequence and add it to your email marketing platform as a second automated sequence.

3. Use AskFint to review and refine the copy. Make any suggested improvements.

4. Set a reminder to send your first broadcast email to your list within 7 days of completing this lesson. It can be as simple as sharing a useful tip from your niche — the goal is to show up consistently.`
        }
      ]
    },
    {
      moduleNumber: 5,
      title: "The Launch & Scale Blueprint",
      overview: "You have a product, a sales page, and an email funnel. Now it's time to launch. This module gives you a complete, step-by-step launch plan — and then shows you how to use AI to scale what's working so your revenue grows month after month.",
      learningOutcome: "By the end of this module, you will have completed your first product launch, analysed the results, and have a clear plan for scaling your revenue using the platform's tools.",
      lessons: [
        {
          lessonNumber: "5.1",
          title: "The 14-Day Launch Plan",
          duration: "~12 minutes",
          toolFeatured: "Launch Advisor",
          isLocked: false,
          requiredToolKey: "launch-advisor",
          driveFileId: "1yrq6nhVJZBpmpO2aoLsFz0pnVfDISwMh",
          summary: "Use Launch Advisor to generate a personalised, day-by-day 14-day launch campaign — from awareness content to final cart close — customised to your product, audience size, and platform.",
          homeworkContent: `1. Open the Launch Advisor and generate your personalised 14-day launch plan.

2. Block out the 14 days in your calendar. Treat each day's tasks as non-negotiable appointments.

3. Prepare your launch content in advance — write your emails, draft your social media posts, and record any videos before Day 1. The more you prepare upfront, the less stressful the launch will be.`
        },
        {
          lessonNumber: "5.2",
          title: "Creating Launch Content with Nexis AI",
          duration: "~10 minutes",
          toolFeatured: "Nexis AI",
          isLocked: false,
          driveFileId: "1IdYQdzURyukzqGJ3uS9zyvnfN2_-7EhB",
          summary: "Create your full launch content stack — awareness, education, announcement, objection-handling, and urgency scripts — using Nexis AI, plus learn how Clip AI can multiply your reach across platforms.",
          homeworkContent: `1. Open Nexis AI and create all 5 launch content scripts as described in this lesson.

2. Record all 5 videos before your launch begins. Batch recording is more efficient — set up your camera once and record all five in a single session.

3. Schedule your content to go live on the days specified in your launch plan.`
        },
        {
          lessonNumber: "5.3",
          title: "Analysing Your Launch Results",
          duration: "~8 minutes",
          toolFeatured: "AskFint",
          isLocked: false,
          driveFileId: "1az2XpddTDaNHIPr4p-CEyTB5mFBAUmzF",
          summary: "Learn the four critical launch metrics — opt-in rate, email open rate, sales page conversion rate, and total revenue — and use AskFint to get a data-driven post-launch analysis with prioritised improvements.",
          homeworkContent: `1. Collect your four key launch metrics: opt-in rate, email open rate, sales page conversion rate, and total revenue.

2. Take your metrics into AskFint and ask for a post-launch analysis and improvement recommendations.

3. Write down your top 3 improvements to make before your next launch. These become your priorities for the next 30 days.`
        },
        {
          lessonNumber: "5.4",
          title: "Scaling: From One Launch to Consistent Monthly Revenue",
          duration: "~13 minutes",
          toolFeatured: "All platform tools",
          isLocked: false,
          driveFileId: "1OpRkcFZW_2wI7kqFbO275jQoJvMeV-i-",
          summary: "Discover the three ways to scale — launching more often, increasing average order value with upsells, and building recurring revenue — plus your 90-day plan for turning a single launch into a sustainable business.",
          homeworkContent: `1. Write your 90-day scaling plan. Include: your next launch date, your email list growth target, and one new product or upsell you want to build.

2. Set up a weekly review habit. Every Monday, spend 20 minutes reviewing your key metrics: email list size, open rate, sales page traffic, and revenue. Use AskFint to help you interpret the data and decide what to focus on that week.

3. Come back to this platform regularly. The tools are here to support every stage of your growth — use them. The creators who get the best results are the ones who treat this platform as their business operating system, not a one-time resource.

Congratulations on completing the course. You now have everything you need to build a creator business that pays you consistently. The only thing left is to do the work. Start today.`
        }
      ]
    }
  ]
};
